import i18n from '@/i18n';
import Notify from '@/components/Notification';
import { useUserStore } from '@/store/userStore';
import { clearLocalSession, logoutUser } from '@/store/services/userServices';
import { isRefreshRequest, refreshAccessToken } from '@/store/tokenRefresh';
import axios, { getRequestHeaderParams } from '../axios-api';
import store from './rootStore';

axios.interceptors.request.use(async config => {
  try {
    const headerParams = getRequestHeaderParams();
    config.headers = {
      ...config.headers,
      ...headerParams,
    };

    const token = useUserStore.getState().accessToken;

    if (token) {
      config.headers.Authorization = `Token ${token}`;
      config.headers['Accept-language'] = i18n.language;
    }
  } catch (e) {
    console.error(e.message);
    // do nothing, user is not logged in
  }

  return config;
});

axios.interceptors.response.use(
  response => response,
  async error => {
    const status = error.response?.status;
    const requestID = error.config?.headers?.['X-Request-ID'];

    if (status === 500) {
      const text = `Error: ${error.response.statusText}\nError code: ${status}\nRequest ID: ${requestID}\nPlease, send this code to support.`;
      Notify.errorSupport({ text });
    }

    if (status === 401 && error.response?.data?.message === 'access_token expired') {
      const originalConfig = error.config;

      // A request that still reports an expired access token after a
      // successful refresh must not start another refresh round.
      if (originalConfig._isRetriedAfterRefresh) {
        clearLocalSession();
        return Promise.reject(error);
      }

      return refreshAccessToken().then(
        () => {
          originalConfig._isRetriedAfterRefresh = true;
          // Let the request interceptor re-attach the fresh token.
          delete originalConfig.headers.Authorization;
          return axios(originalConfig);
        },
        err => {
          // A failed refresh means this client has no reachable server
          // session; POSTing /logout/ here would clear the shared refresh
          // cookie a sibling tab just rotated and kill its live session.
          clearLocalSession();
          return Promise.reject(err);
        },
      );
    }

    // Gate on a local session rather than on the URL: once the tokens are
    // gone the next request 401s without any Authorization header, and
    // POSTing /logout/ then would clear the refresh cookie shared with
    // sibling tabs and kill their live sessions.
    if (
      status === 401 &&
      useUserStore.getState().accessToken &&
      !isRefreshRequest(error.config)
    ) {
      logoutUser();
    }

    return error.response ? error.response : { data: { message: error.message } };
  },
);

export default store;
